import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatEur } from "@/lib/api";
import { BrandHeader } from "@/components/BrandHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { AgeBadges, AGE_CATEGORIES, ADULT_CATEGORIES, FAMILY_CATEGORIES, FAMILY_FILTER, matchesAgeFilter } from "@/lib/ageCategories";
import { CalendarDays, List, ChevronLeft, ChevronRight, MapPin, Clock, Info, X, Users, User } from "lucide-react";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DOW = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function parseDate(s) { return s ? new Date(s + "T00:00:00") : null; }
function fmtDateRange(a) {
  const s = parseDate(a.start_date), e = parseDate(a.end_date);
  const opt = { day: "numeric", month: "long" };
  if (!s) return "";
  if (a.end_date && a.end_date !== a.start_date)
    return `${s.toLocaleDateString("es-ES", opt)} – ${e.toLocaleDateString("es-ES", opt)}`;
  return s.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
}

function ActivityCard({ a, onOpen, onSignup }) {
  const canSignup = a.status !== "cancelada" && !a.inscriptions_closed && !a.is_full;
  const minPrice = Math.min(...(a.tariffs?.map((t) => t.amount) || [0]));
  return (
    <div data-testid={`activity-card-${a.id}`} className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl border border-slate-100 transition-transform hover:-translate-y-1 duration-200 flex flex-col">
      <div className="relative h-48 overflow-hidden">
        <img src={a.photos?.[0]} alt={a.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute top-3 left-3"><StatusBadge activity={a} /></div>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="text-xs font-bold text-[#FF7F27] uppercase tracking-wide">{fmtDateRange(a)}</p>
        <h3 className="text-lg font-black brand-navy mt-1 leading-tight">{a.title}</h3>
        <div className="mt-2"><AgeBadges ids={a.age_categories} /></div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-2">
          <span className="flex items-center gap-1"><Clock size={13} />{a.schedule}</span>
          <span className="flex items-center gap-1"><MapPin size={13} />{a.place}</span>
        </div>
        <p className="text-sm text-slate-600 mt-3 line-clamp-4 leading-relaxed flex-1">{a.short_description}</p>
        <div className="flex items-center justify-between mt-4">
          <div>
            <span className="text-xs text-slate-400">Desde</span>
            <p className="text-xl font-black brand-navy">{formatEur(minPrice)}</p>
          </div>
          {a.is_full && <span className="text-xs font-bold text-slate-600">Completa</span>}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button data-testid={`more-info-${a.id}`} onClick={() => onOpen(a)} className="flex items-center justify-center gap-1.5 border-2 border-[#003284] text-[#003284] font-bold rounded-full py-2.5 text-sm hover:bg-[#003284] hover:text-white transition-colors">
            <Info size={15} /> Más info
          </button>
          <button data-testid={`signup-${a.id}`} disabled={!canSignup} onClick={() => onSignup(a)} className="bg-brand-orange text-white font-bold rounded-full py-2.5 text-sm hover:bg-[#DE7A1C] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            Apuntarse
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(window.innerWidth < 768 ? "lista" : "calendario");
  const [cursor, setCursor] = useState(new Date());
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const edad = searchParams.get("edad");
  const edadCat = edad === "familiares" ? FAMILY_FILTER : AGE_CATEGORIES.find((c) => c.id === edad);

  useEffect(() => {
    api.get("/activities").then((r) => setActivities(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const onOpen = (a) => navigate(`/actividad/${a.id}`);
  const onSignup = (a) => navigate(`/actividad/${a.id}/apuntarse`);

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const monthActs = useMemo(() => activities.filter((a) => {
    const s = parseDate(a.start_date);
    return s && s.getFullYear() === year && s.getMonth() === month;
  }), [activities, year, month]);

  const filteredByAge = useMemo(() =>
    edad ? activities.filter((a) => matchesAgeFilter(a.age_categories, edad)) : [],
    [activities, edad]);

  const grid = useMemo(() => {
    const first = new Date(year, month, 1);
    const startDow = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [year, month]);

  const actsOnDay = (d) => activities.filter((a) => {
    const s = parseDate(a.start_date), e = parseDate(a.end_date || a.start_date);
    if (!s) return false;
    const day = new Date(year, month, d);
    return day >= new Date(s.getFullYear(), s.getMonth(), s.getDate()) &&
           day <= new Date(e.getFullYear(), e.getMonth(), e.getDate());
  });

  // ---- Age-filtered view ----
  if (edadCat) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <BrandHeader />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
          <div className="flex items-center gap-3 mb-2">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold ${edadCat.cls}`}>{edadCat.label} · {edadCat.range}</span>
            <button data-testid="clear-age-filter" onClick={() => setSearchParams({})} className="flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-[#E31837]"><X size={15} /> Ver todas</button>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black brand-navy">Actividades para {edadCat.label}</h1>
          <p className="text-slate-500 mt-1 mb-6">Todas las actividades disponibles para este rango de edad.</p>
          {loading ? <p className="text-center text-slate-400 py-20">Cargando…</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredByAge.length === 0 && <p className="text-slate-400 col-span-full text-center py-10">No hay actividades para este rango de edad.</p>}
              {filteredByAge.map((a) => <ActivityCard key={a.id} a={a} onOpen={onOpen} onSignup={onSignup} />)}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <BrandHeader />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black brand-navy">Calendario de actividades</h1>
            <p className="text-slate-500 mt-1">Elige una actividad y apúntate en unos minutos.</p>
          </div>
          <div className="flex bg-white rounded-full p-1 border border-slate-200 self-start">
            <button data-testid="view-calendar-btn" onClick={() => setView("calendario")} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-colors ${view === "calendario" ? "bg-brand-navy text-white" : "text-slate-500"}`}><CalendarDays size={16} /> Calendario</button>
            <button data-testid="view-list-btn" onClick={() => setView("lista")} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-colors ${view === "lista" ? "bg-brand-navy text-white" : "text-slate-500"}`}><List size={16} /> Lista</button>
          </div>
        </div>

        {/* Filtros rápidos por edad */}
        <div className="grid sm:grid-cols-2 gap-3 mb-6">
          <div className="rounded-2xl border-2 border-[#003284]/25 bg-[#003284]/[0.05] p-3">
            <div className="flex items-center gap-2 mb-2.5">
              <Users size={16} className="text-[#003284]" />
              <span className="text-xs font-black uppercase tracking-wide text-[#003284]">Adultos</span>
              <span className="text-[11px] font-semibold text-slate-500">— solo mayores</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {ADULT_CATEGORIES.map((c) => (
                <button key={c.id} data-testid={`filter-age-${c.id}`} onClick={() => setSearchParams({ edad: c.id })} className="rounded-full px-3 py-1.5 text-sm font-bold border-2 border-slate-200 text-slate-600 hover:border-[#003284] hover:text-[#003284] transition-colors">{c.label} <span className="opacity-60">{c.range}</span></button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border-2 border-[#FF7F27]/35 bg-[#FF7F27]/[0.06] p-3">
            <div className="flex items-center gap-2 mb-2.5">
              <span className="flex items-end text-[#FF7F27] -space-x-0.5"><User size={18} strokeWidth={2.25} /><User size={12} strokeWidth={2.25} /></span>
              <span className="text-xs font-black uppercase tracking-wide text-[#FF7F27]">Familiares</span>
              <span className="text-[11px] font-semibold text-slate-500">— elige edad o todas</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button data-testid="filter-age-familiares" onClick={() => setSearchParams({ edad: "familiares" })} className="rounded-full px-3 py-1.5 text-sm font-bold border-2 border-slate-200 text-slate-600 hover:border-[#FF7F27] hover:text-[#FF7F27] transition-colors">Todas las familiares</button>
              {FAMILY_CATEGORIES.map((c) => (
                <button key={c.id} data-testid={`filter-age-${c.id}`} onClick={() => setSearchParams({ edad: c.id })} className="rounded-full px-3 py-1.5 text-sm font-bold border-2 border-slate-200 text-slate-600 hover:border-[#FF7F27] hover:text-[#FF7F27] transition-colors">{c.label} <span className="opacity-60">{c.range}</span></button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 mb-6">
          <button data-testid="prev-month-btn" onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50"><ChevronLeft size={20} /></button>
          <h2 className="text-xl font-black brand-navy w-56 text-center">{MONTHS[month]} {year}</h2>
          <button data-testid="next-month-btn" onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-2 rounded-full bg-white border border-slate-200 hover:bg-slate-50"><ChevronRight size={20} /></button>
        </div>

        {loading ? (
          <p className="text-center text-slate-400 py-20">Cargando actividades…</p>
        ) : view === "calendario" ? (
          <div className="bg-white rounded-3xl p-3 sm:p-5 border border-slate-100 shadow-sm overflow-x-auto">
            <div className="grid grid-cols-7 gap-2 min-w-[640px]">
              {DOW.map((d) => <div key={d} className="text-center text-xs font-bold text-slate-400 pb-2">{d}</div>)}
              {grid.map((d, i) => (
                <div key={i} className={`min-h-[150px] rounded-2xl p-2 ${d ? "bg-slate-50" : ""}`}>
                  {d && <span className="text-xs font-bold text-slate-400">{d}</span>}
                  <div className="space-y-1.5 mt-1">
                    {d && actsOnDay(d).map((a) => (
                      <button key={a.id} data-testid={`cal-act-${a.id}`} onClick={() => onOpen(a)} className="w-full text-left rounded-lg overflow-hidden bg-white border border-slate-200 hover:border-[#FF7F27] hover:shadow-md transition-all">
                        <div className="relative h-10 w-full">
                          <img src={a.photos?.[0]} alt="" className="w-full h-full object-cover" />
                          {a.status === "cancelada" && <span className="absolute inset-0 bg-[#E31837]/75 text-white text-[9px] font-black flex items-center justify-center uppercase">Cancelada</span>}
                          {a.is_full && a.status !== "cancelada" && <span className="absolute inset-0 bg-slate-800/60 text-white text-[9px] font-black flex items-center justify-center uppercase">Completa</span>}
                        </div>
                        <div className="px-1.5 py-1">
                          <p className="text-[10px] font-bold text-[#003284] leading-tight line-clamp-2">{a.title}</p>
                          <div className="mt-0.5"><AgeBadges ids={a.age_categories} compact /></div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {monthActs.length === 0 && <p className="text-slate-400 col-span-full text-center py-10">No hay actividades este mes.</p>}
            {monthActs.map((a) => <ActivityCard key={a.id} a={a} onOpen={onOpen} onSignup={onSignup} />)}
          </div>
        )}

        {view === "calendario" && monthActs.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-black brand-navy mb-4">Actividades de {MONTHS[month]}</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {monthActs.map((a) => <ActivityCard key={a.id} a={a} onOpen={onOpen} onSignup={onSignup} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
